import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { NavController, NavParams } from "ionic-angular";
import { Subscription } from "rxjs/Subscription";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { CubeMenu } from "../../../app/access-control/permissions";
import { NavigationService } from "../../../navigation/navigation.service";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../providers/app-status/app-status";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

/**
 * Generated class for the BillingNavigationPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

@Component({
  selector: "billing-navigation",
  templateUrl: "billing-navigation.html",
})
export class BillingNavigation implements OnDestroy {
  @Output()
  navigated = new EventEmitter<string>();

  // CO-3013: these subscriptions target StateManagementService BehaviorSubjects,
  // which are app-level singletons. Without teardown a fresh subscription leaks
  // every time the billing shell (and this side-nav) is re-mounted. Store them
  // so ngOnDestroy can release them.
  private billingReportsSub: Subscription;
  private scheduledEligibilitySub: Subscription;
  private monthEndCloseSub: Subscription;

  // STIN-2808: hiding sections (temp), except ClaimManagement & PaymentManagement
  private _claimManagementEnabled: boolean = true;
  private _paymentProcessingEnabled: boolean = true;
  private _remittanceManagementEnabled: boolean = true;
  private _accountsReceivableEnabled: boolean = false;
  private _monthEndCloseEnabled: boolean = false;
  private _activePage: string = "ClaimManagementPage";
  showBillingReports: boolean = true;
  showScheduledEligibility: boolean = false;
  private _eightThirtySevenManagementEnabled: boolean = false;

  // CO-3457 Overlap Review nav entry - copies the Open Visits nav-gating
  // pattern (dashboard-navigation-bar.ts checkOpenVisitsTogglePermission):
  // resolved in TS from the toggle + a permission ask, never in the
  // template. "online" is checked inline in the template, matching that
  // same precedent (appSyncStatus !== connectionStatusEnum.Offline).
  concurrentVisitsToggle: boolean = false;
  displayOverlapReviewButton: boolean = false;
  appSyncStatus: ConnectionStatusEnum;
  connectionStatusEnum = ConnectionStatusEnum;
  private overlapReviewAccess: boolean = false;
  private concurrentVisitsToggleSub: Subscription;
  private overlapReviewPermissionSub: Subscription;

  constructor(
    public navCtrl: NavController,
    private navigationService: NavigationService,
    private navBarService: NavBarServiceProvider,
    private state: StateManagementService,
    public navParams: NavParams,
    public stateData: StateManagementService,
    private accessControlService: AccessControlService,
    private appStatus: AppStatus
  ) {
    this.billingReportsSub = this.stateData.showBillingReports.subscribe(
      this.onShowBillingReports.bind(this)
    );
    this.validateCloseMonthGridToggle();
    this.showBillingReports = true;

    this.scheduledEligibilitySub =
      this.stateData.showScheduledEligibility.subscribe(
        this.onShowScheduledEligibility.bind(this)
      );

    this.appSyncStatus = this.appStatus.getAppStatus();
    this.concurrentVisitsToggleSub =
      this.stateData.concurrentVisitsToggle.subscribe((toggle) => {
        // Null (never fetched) and false both resolve to "off" per contract.
        this.concurrentVisitsToggle = !!toggle;
        this.checkOverlapReviewButtonVisibility();
      });
    this.overlapReviewPermissionSub = this.accessControlService
      .getPermissionWhenAuthenticated([CubeMenu.Billing])
      .subscribe(
        (data) => {
          this.overlapReviewAccess = this.accessControlService.hasAccess(
            data,
            CubeMenu.Billing
          );
          this.checkOverlapReviewButtonVisibility();
        },
        (err) => {
          console.error(
            "Billing permission ask failed; Overlap Review nav entry stays hidden",
            err
          );
        }
      );
  }

  private checkOverlapReviewButtonVisibility() {
    this.displayOverlapReviewButton =
      this.concurrentVisitsToggle && this.overlapReviewAccess;
  }

  ngOnDestroy(): void {
    if (this.billingReportsSub) {
      this.billingReportsSub.unsubscribe();
    }
    if (this.scheduledEligibilitySub) {
      this.scheduledEligibilitySub.unsubscribe();
    }
    if (this.monthEndCloseSub) {
      this.monthEndCloseSub.unsubscribe();
    }
    if (this.concurrentVisitsToggleSub) {
      this.concurrentVisitsToggleSub.unsubscribe();
    }
    if (this.overlapReviewPermissionSub) {
      this.overlapReviewPermissionSub.unsubscribe();
    }
  }

  protected onShowBillingReports(newValue): void {
    this.showBillingReports = newValue;
  }

  protected onShowScheduledEligibility(newValue): void {
    this.showScheduledEligibility = newValue;
  }

  redirectPage(entity: string) {
    let page = "";
    let navParams = {};

    switch (entity) {
      case "paymentManagement":
        page = "PaymentManagementPage";
        break;
      case "accountsReceivable":
        page = "AccountsReceivablePage";
        break;
      case "remittanceManagement":
        page = "RemittanceManagementPage";
        break;
      case "eightThirtySevenManagement":
        page = "EightThirtySevenManagementPage";
        break;
      case "monthEndClose":
        page = "MonthEndClosePage";
        break;
      case "heldARActivities":
        page = "HeldARActivitiesPage";
        break;
      case "billingReports":
        page = "BillingReportsPage";
        break;
      case "billingEligibility":
        page = "BillingEligibilityPage";
        break;
      case "overlapReview":
        page = "OverlapReviewPage";
        break;
      case "claimManagement":
      default:
        page = "ClaimManagementPage";
    }

    this.navigationService.goToInternalPage(page, navParams);
  }

  @Input()
  set activePage(value: string) {
    this._activePage = value;
  }

  @Input()
  set claimManagementEnabled(value: boolean) {
    this._claimManagementEnabled = value;
  }

  @Input()
  set remittanceManagementEnabled(value: boolean) {
    this._remittanceManagementEnabled = value;
  }

  @Input()
  set eightThirtySevenManagementEnabled(value: boolean) {
    this._eightThirtySevenManagementEnabled = value;
  }

  @Input()
  set paymentProcessingEnabled(value: boolean) {
    this._paymentProcessingEnabled = value;
  }

  @Input()
  set accountsReceivableEnabled(value: boolean) {
    this._accountsReceivableEnabled = value;
  }

  @Input()
  set monthEndCloseEnabled(value: boolean) {
    this._monthEndCloseEnabled = value;
  }

  get activePage(): string {
    return this._activePage;
  }

  get claimManagementEnabled(): boolean {
    return this._claimManagementEnabled;
  }

  get remittanceManagementEnabled(): boolean {
    return this._remittanceManagementEnabled;
  }

  get eightThirtySevenManagementEnabled(): boolean {
    return this._eightThirtySevenManagementEnabled;
  }

  get paymentProcessingEnabled(): boolean {
    return this._paymentProcessingEnabled;
  }

  get accountsReceivableEnabled(): boolean {
    return this._accountsReceivableEnabled;
  }

  get monthEndCloseEnabled(): boolean {
    return this._monthEndCloseEnabled;
  }

  validateCloseMonthGridToggle(): void {
    this.monthEndCloseSub = this.state.monthEndClose.subscribe((toggleOn) => {
      if (toggleOn !== null) {
        this._monthEndCloseEnabled = toggleOn;
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/billing/billing-navigation/billing-navigation.ts